// components/ui/ModeChip.tsx
//
// The composer mode picker, first built for Source: a coloured chip on the composer row and a small
// popover headed "Mode" listing the positions, the active one checked, digits selecting a row.
// Callers own what a mode means; this only shows and picks one.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from '@/components/ui/lucide-memo';

// globals.css:390 forces `div, label, button, input, span` to 12px outside any @layer, which beats
// every Tailwind text-[..] utility. Every type size below is therefore an inline style.
const CHIP_FONT_SIZE = 11;
const ROW_LABEL_FONT_SIZE = 14;
const HEADER_FONT_SIZE = 13;

// ChatGPT's selected chip: a quiet white wash with bright text, the app's one chip colour.
const PILL_FG = '#ECECEC';
const PILL_BG = 'rgba(255, 255, 255, 0.08)';

type ModeChipProps<M extends string> = {
  modes: readonly M[];
  mode: M;
  labels: Record<M, string>;
  header: string;
  onSelect: (mode: M) => void;
  disabled?: boolean;
  /** A round 32px pill level with the composer's 36px buttons (Source's ChatGPT composer). */
  pill?: boolean;
  /** What the chip itself reads, when the active row's label alone would not say what it is ("Budget: 1 h"). */
  chipLabel?: string;
  /** A right click on the chip (the Modelist's budget: switching between time and credits). */
  onContextMenu?: (event: React.MouseEvent) => void;
};

export function ModeChip<M extends string>({ modes, mode, labels, header, onSelect, disabled = false, pill = false, chipLabel, onContextMenu }: ModeChipProps<M>) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // The composer card is `overflow-hidden` (it has to be: the textarea and the image strip grow
  // inside it), so an absolutely positioned menu is clipped to the card and the user sees a sliver.
  // Every other popover on this row portals for the same reason. Anchor it to the trigger's rect
  // and pin it above, the way CollaborationModeSelector does on the Studio composer.
  const [anchor, setAnchor] = useState<{ left: number; bottom: number } | null>(null);
  const [mounted, setMounted] = useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
  useEffect(() => setMounted(true), []);

  const selectMode = useCallback(
    (next: M) => {
      setOpen(false);
      if (next !== mode) onSelect(next);
    },
    [mode, onSelect]
  );

  // Outside click + Escape, plus the 1/2/3 the rows advertise. The digits are bound only while the
  // popover is open, the same way the proposal card binds its numbered choices: the app has no
  // global hotkey layer outside the Studio canvas, and inventing one for a chip would be a bigger
  // change than the chip.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        return;
      }
      const index = Number.parseInt(event.key, 10);
      if (Number.isInteger(index) && index >= 1 && index <= modes.length) {
        event.preventDefault();
        selectMode(modes[index - 1]!);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, selectMode]);

  const toggle = useCallback(() => {
    setOpen((value) => {
      if (value) return false;
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setAnchor({ left: Math.round(rect.left), bottom: Math.round(window.innerHeight - rect.top + 8) });
      return true;
    });
  }, []);

  return (
    <div className="relative shrink-0" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        onContextMenu={onContextMenu}
        disabled={disabled}
        className={`flex items-center justify-center font-medium leading-none transition-colors disabled:opacity-40 ${pill ? 'h-8 rounded-full px-3' : 'h-[22px] rounded-[6px] px-[7px]'}`}
        style={{
          fontSize: pill ? 13 : CHIP_FONT_SIZE,
          color: PILL_FG,
          backgroundColor: PILL_BG,
        }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span style={{ fontSize: pill ? 13 : CHIP_FONT_SIZE, lineHeight: 1 }}>{chipLabel ?? labels[mode]}</span>
      </button>

      {open && mounted && anchor
        ? createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="fixed z-[9999] p-1.5"
          style={{
            bottom: anchor.bottom,
            left: anchor.left,
            minWidth: 200,
            // A long list (the Modelist's fifteen time budgets) scrolls rather than leaving the screen.
            maxHeight: '70vh',
            overflowY: 'auto',
            backgroundColor: '#353535',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            borderRadius: 16,
            boxShadow: '0 18px 52px rgba(0, 0, 0, 0.5)',
          }}
        >
          <div className="px-3 pb-1 pt-1.5" style={{ fontSize: HEADER_FONT_SIZE, color: '#AFAFAF' }}>
            {header}
          </div>

          {modes.map((candidate, index) => {
            const active = candidate === mode;
            return (
              <button
                key={candidate}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => selectMode(candidate)}
                className="flex w-full items-center gap-3 rounded-[12px] px-3 py-2 text-left transition-colors hover:bg-white/[0.08]"
              >
                {/* Label left, number right, and the number is REPLACED by the check on the active
                    row. No second line: the popover is a switch, not documentation. */}
                <span className="min-w-0 flex-1 truncate" style={{ fontSize: ROW_LABEL_FONT_SIZE, color: '#ECECEC' }}>
                  {labels[candidate]}
                </span>
                <span className="flex w-4 shrink-0 items-center justify-end">
                  {active ? (
                    <Check size={16} style={{ color: '#ECECEC' }} />
                  ) : (
                    <span style={{ fontSize: ROW_LABEL_FONT_SIZE, color: '#8E8E8E' }}>{index + 1}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>,
            document.body
          )
        : null}
    </div>
  );
}
